import React from 'react';
import { Report } from '../hooks/useReports';
import TreasureCard from './TreasureCard';
import { SkeletonGrid } from './LoadingStates';
import { EmptyState } from './EmptyStates';
import { TreasureCardAd } from './ads/TreasureCardAd';
import { FeatureGate } from './FeatureGate';
import { shouldShowAd, getAdPosition } from '../utils/adPlacement';

interface TreasureGridProps {
  reports: Report[];
  isLoading?: boolean;
  onFavorite?: (reportId: string, isFavorited: boolean) => void;
  favoriteReportIds?: Set<string>;
  showCoins?: boolean;
  onShare?: (reportId: string) => void;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateAction?: {
    label: string;
    onClick: () => void;
  };
}

const TreasureGrid: React.FC<TreasureGridProps> = ({
  reports,
  isLoading = false,
  onFavorite,
  favoriteReportIds = new Set(),
  showCoins = true,
  onShare,
  emptyStateTitle = "No treasures found",
  emptyStateDescription = "Be the first to share a treasure with the community!",
  emptyStateAction
}) => {


  return (
    <div className="w-full">
      {/* Responsive Grid with Auto-Fill Columns */}
      {/* Loading skeletons */}
      {isLoading && <SkeletonGrid count={8} type="card" />}
      
      {/* Treasure cards grid with ads - Mobile Optimized */}
      {!isLoading && reports.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
          {reports.map((report, index) => (
            <React.Fragment key={report.id}>
              <TreasureCard
                report={report}
                onFavorite={onFavorite}
                isFavorited={favoriteReportIds.has(report.id)}
                showCoins={showCoins}
                onShare={onShare}
              />
              
              {/* Insert ad after every 4th treasure card */}
              {shouldShowAd(index) && (
                <FeatureGate flag="GOOGLE_ADS_ENABLED" key={`ad-${getAdPosition(index)}`}>
                  <TreasureCardAd 
                    position={getAdPosition(index)}
                    className="treasure-card-ad-grid-item"
                  />
                </FeatureGate>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      
      {/* Empty state */}
      {!isLoading && reports.length === 0 && (
        <EmptyState
          icon="🔍"
          title={emptyStateTitle}
          description={emptyStateDescription}
          action={emptyStateAction}
        />
      )}
    </div>
  );
};

export default TreasureGrid;